import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { Loading } from '../components/Loading';
import { Toast, ToastProps } from '../components/Toast';

/** A shell's token scope, never the stack's own copy of it. */
const SHELL_SCOPE = '.forge-root[data-forge-theme]:not(.notification-toast-stack)';

/**
 * The theme of whichever shell is mounted. Control is pinned light and the login screen has no
 * shell at all, so light is the answer when nothing is found.
 */
function readShellTheme(): string {
  return document.querySelector<HTMLElement>(SHELL_SCOPE)?.dataset.forgeTheme ?? 'light';
}

interface NotificationContextValues {
  showToast: (c: ToastProps, duration?: number) => void;
  toggleLoading: (value: boolean) => void;
  showErrorToast: (message: string, title?: string, duration?: number) => void;
  showInfoToast: (message: string, title?: string, duration?: number) => void;
  showSuccessToast: (message: string, title?: string, duration?: number) => void;
  showWarningToast: (message: string, title?: string, duration?: number) => void;
}

export const NotificationContext = createContext<NotificationContextValues>({
  showToast: () => {
    throw new Error('showToast function not implemented');
  },
  toggleLoading: () => {
    throw new Error('toggleLoading function not implemented');
  },
  showErrorToast: () => {
    throw new Error('showErrorToast function not implemented');
  },
  showInfoToast: () => {
    throw new Error('showErrorToast function not implemented');
  },
  showSuccessToast: () => {
    throw new Error('showSuccessToast function not implemented');
  },
  showWarningToast: () => {
    throw new Error('showWarningToast function not implemented');
  },
});

type NotificationContextProviderProps = PropsWithChildren<{}>;

export const NotificationContextProvider: React.FC<NotificationContextProviderProps> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [toastStack, setToastStack] = useState<ToastProps[]>([]);
  // This provider is mounted above the router, outside every shell, and `var(--danger)` does
  // not resolve outside `.forge-root[data-forge-theme]`. The stack therefore carries the token
  // scope itself and mirrors the mounted shell's theme, rather than being portaled into that
  // shell: a portal would be torn out of the document whenever the shell unmounts in the same
  // commit that raises the toast, which is exactly what `logout()` does.
  const [scopeTheme, setScopeTheme] = useState(readShellTheme);

  // Re-read after the commit that changes the stack, not during the render that queues it: by
  // then a shell that this toast is unmounting (logout, an expired session) is already gone.
  useLayoutEffect(() => {
    setScopeTheme((current) => {
      const next = readShellTheme();
      return current === next ? current : next;
    });
  }, [toastStack]);

  // Nothing re-renders this provider when the shell changes underneath it, so watch the DOM
  // while a toast is on screen: `data-forge-theme` for the theme toggle, and node insertion for
  // a route that swaps one shell for another, which arrives with the attribute already set and
  // so produces no attribute record. The observer coalesces a whole commit into one callback,
  // and it is only armed while there is something to recolour.
  const hasToasts = toastStack.length > 0;
  useEffect(() => {
    if (!hasToasts) return undefined;
    const observer = new MutationObserver(() =>
      setScopeTheme((current) => {
        const next = readShellTheme();
        return current === next ? current : next;
      })
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-forge-theme'],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [hasToasts]);

  const showToast = useCallback((c: ToastProps, duration = 3000) => {
    const closeToast = () => {
      setToastStack((prev) => prev.filter((t) => t !== toast));
      c.onClose?.();
    };
    const toast = {
      ...c,
      onClose: closeToast,
    };
    setToastStack((prev) => [...prev, toast]);
    setTimeout(() => {
      setToastStack((prev) => prev.filter((t) => t !== toast));
    }, duration);
  }, []);

  const showErrorToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'error',
        },
        duration
      );
    },
    [showToast]
  );

  const showInfoToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'info',
        },
        duration
      );
    },
    [showToast]
  );

  const showSuccessToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'success',
        },
        duration
      );
    },
    [showToast]
  );

  const showWarningToast = useCallback(
    (message: string, title: string = '', duration = 3000) => {
      showToast(
        {
          title,
          message,
          type: 'warning',
        },
        duration
      );
    },
    [showToast]
  );

  return (
    <NotificationContext.Provider
      value={{
        showToast,
        toggleLoading: setLoading,
        showErrorToast,
        showInfoToast,
        showSuccessToast,
        showWarningToast,
      }}
    >
      {children}
      <div
        className="notification-toast-stack forge-root fixed top-4 right-4 z-50 space-y-3 w-80"
        data-forge-theme={scopeTheme}
        data-testid="notification-theme-scope"
      >
        {toastStack.map((toast, index) => (
          <Toast key={index} {...toast} />
        ))}
      </div>
      <Loading open={loading} />
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextValues =>
  useContext(NotificationContext);
